import { createRequest, HttpMethod } from 'ehs-react';
import type { at } from '../generated/Beans';
import type { icModel } from 'generated-ts-common';
type ViewStateResponse = at.sense.portlets.saf.virtualvisitpatient.model.state.ViewStateResponse;
type ViewStateData = at.sense.portlets.saf.virtualvisitpatient.model.state.ViewStateData;
type InstitutionQuestionnaire = at.sense.portlets.saf.virtualvisitpatient.model.InstitutionQuestionnaire;
type Institution = at.sense.portlets.saf.virtualvisitpatient.model.Institution;
type Questionnaire = icModel.saf.infrastructure.model.virtualvisit.Questionnaire;
type RemoteContent = at.sense.portlets.saf.virtualvisitpatient.model.RemoteContent;
type GuestBean = at.sense.saf.infrastructure.portalutil.virtualvisit.guest.GuestBean;
type CallInfo = at.sense.saf.infrastructure.portalutil.virtualvisit.callinfo.CallInfo;

export interface LoadInitialStateParams {
  userAgent: string;
}

export interface NextStateParam {
  stateData: ViewStateData;
}

export interface LeaveWaitingListParam {
  visitId: string;
}

export interface RetrieveQuestionnaireParam {
  institutionId: string;
}

export interface GetRemoteContentParam {
  institutionId: string;
}

export interface GetInstituationsParam {
  regionId: string;
}

export interface SendQuestionnaireParam {
  visitId: string;
  institutionId: string;
  questionnaire: Questionnaire;
}

export interface VerifyGoogleReCaptchaParam {
  recaptchaResponse: string;
}

export interface InviteGuestsParam {
  visitId: string;
}

export interface ValidateGuestParam {
  visitId: string;
  guestToken: string;
}

export interface GrantBioAnalysisPermissionParams {
  visitId: string;
}

export interface CallInfoParam {
  callId: string;
  callInfo: CallInfo;
  additionalCallInfo: string;
}

export const loadInitialState = createRequest<
  LoadInitialStateParams,
  ViewStateResponse
>({
  operation: 'loadInitialState',
  method: HttpMethod.POST,
  returnsArray: false
});

export const nextState = createRequest<NextStateParam, ViewStateResponse>({
  operation: 'nextState',
  method: HttpMethod.POST,
  returnsArray: false
});

export const previousState = createRequest<void, ViewStateResponse>({
  operation: 'previousState',
  method: HttpMethod.POST,
  returnsArray: false
});

export const inviteGuestToCall = createRequest<InviteGuestsParam, GuestBean>({
  operation: 'inviteGuestToCall',
  method: HttpMethod.POST,
  returnsArray: false
});

export const recreateGuestLink = createRequest<ValidateGuestParam, GuestBean>({
  operation: 'recreateGuestLink',
  method: HttpMethod.POST,
  returnsArray: false
});

export const validateGuest = createRequest<ValidateGuestParam, boolean>({
  operation: 'validateGuest',
  method: HttpMethod.POST,
  returnsArray: false
});

export const leaveWaitingList = createRequest<LeaveWaitingListParam, boolean>({
  operation: 'leaveWaitingList',
  method: HttpMethod.POST,
  returnsArray: false
});

export const retrieveQuestionaire = createRequest<
  RetrieveQuestionnaireParam,
  InstitutionQuestionnaire
>({
  operation: 'retrieveQuestionnaire',
  method: HttpMethod.GET
});

export const getRegions = createRequest<void, Institution[]>({
  operation: 'getRegions',
  method: HttpMethod.GET,
  returnsArray: true
});

export const getInstitutionsByRegionId = createRequest<
  GetInstituationsParam,
  Institution[]
>({
  operation: 'getInstitutionsFromRegionId',
  method: HttpMethod.GET,
  returnsArray: true
});

export const getHospitalsByRegionId = createRequest<
  GetInstituationsParam,
  Institution[]
>({
  operation: 'getHospitalsByRegionId',
  method: HttpMethod.GET,
  returnsArray: true
});

export const sendQuestionnaire = createRequest<SendQuestionnaireParam, void>({
  operation: 'sendQuestionnaire',
  method: HttpMethod.POST,
  returnsArray: false
});

export const verifyGoogleReCaptcha = createRequest<
  VerifyGoogleReCaptchaParam,
  boolean
>({
  operation: 'verifyGoogleRecaptcha',
  method: HttpMethod.POST,
  returnsArray: false
});

export const getRemoteContent = createRequest<
  GetRemoteContentParam,
  RemoteContent[]
>({
  operation: 'getRemoteContent',
  method: HttpMethod.GET,
  returnsArray: true
});

export const grantBioAnalysisPermission = createRequest<
  GrantBioAnalysisPermissionParams,
  void
>({
  operation: 'grantBioAnalysisPermission',
  method: HttpMethod.POST,
  returnsArray: false
});

export const callInfo = createRequest<CallInfoParam, void>({
  operation: 'callInfo',
  method: HttpMethod.POST,
  returnsArray: false
});
